// CAP-2: native-aware auth helpers.
//
// Firebase's web SDK popup/redirect sign-in is unreliable inside a Capacitor
// WKWebView. On a native platform we use @capacitor-firebase/authentication to
// run the OS-native sign-in sheet, then bridge the returned credential into the
// JS Firebase SDK with signInWithCredential — so the rest of the app, which
// reads the shared `auth` object, keeps working unchanged. On web we keep the
// existing popup flow.
//
// The plugin is dynamically imported so it is only pulled in on device and the
// web bundle never depends on it.
//
// REMAINING NATIVE SETUP (owned by whoever holds the Firebase project — see
// docs/CAP-2-native-auth.md): add GoogleService-Info.plist (iOS) /
// google-services.json (Android), the iOS URL scheme (REVERSED_CLIENT_ID),
// enable the providers in the Firebase console, then `npx cap sync`.

import { Capacitor } from "@capacitor/core";
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
} from "firebase/auth";
import { auth, googleProvider } from "./config";

export const isNativePlatform = Capacitor.isNativePlatform();

/**
 * Google sign-in that works in the native WebView and on the web.
 * Returns a Firebase UserCredential either way.
 */
export async function signInWithGoogleUnified() {
  if (!isNativePlatform) {
    return signInWithPopup(auth, googleProvider);
  }

  const { FirebaseAuthentication } = await import(
    "@capacitor-firebase/authentication"
  );
  const result = await FirebaseAuthentication.signInWithGoogle();
  const credential = GoogleAuthProvider.credential(
    result.credential?.idToken,
    result.credential?.accessToken,
  );
  return signInWithCredential(auth, credential);
}

/**
 * Keep the native Firebase layer signed out in step with the JS SDK. Safe no-op
 * on web.
 */
export async function nativeSignOut() {
  if (!isNativePlatform) return;
  try {
    const { FirebaseAuthentication } = await import(
      "@capacitor-firebase/authentication"
    );
    await FirebaseAuthentication.signOut();
  } catch {
    /* plugin unavailable */
  }
}
